import { AfterRenderRef, AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, afterNextRender, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-two-factor-input',
    templateUrl: './two-factor-input.component.html',
    styleUrls: ['./two-factor-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TwoFactorInputComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TwoFactorInputComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() style: 'compact' | 'spread' = 'spread';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() maxLength: number = 6;
  @Input() autoFocus: boolean = false;

  @Output() codeComplete = new EventEmitter<string>();
  @Output() codeChange = new EventEmitter<string>();

  value: string = '';
  digits: string[] = [];
  private componentId: string;
  private scheduledFocusRef?: AfterRenderRef;
  private scheduledSyncRef?: AfterRenderRef;
  
  private onChange = (value: string) => {};
  onTouched = () => {};

  constructor(
    private readonly injector: Injector,
    private readonly destroyRef: DestroyRef
  ) {
    // Generate a unique component ID once
    this.componentId = Math.random().toString(36).substr(2, 9);
  }

  ngOnInit(): void {
    this.initializeDigits();
  }

  ngAfterViewInit(): void {
    if (this.autoFocus && !this.disabled) {
      this.focusFirstInput();
    }
  }

  ngOnDestroy(): void {
    this.clearScheduledFocus();
    this.clearScheduledSync();
  }

  private initializeDigits(): void {
    this.digits = new Array(this.maxLength).fill('');
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value = value || '';
    this.updateDigitsFromValue();
    this.scheduleSyncInputs();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Event handlers
  onDigitInput(event: Event, index: number): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;

    // Only allow digits
    if (!/^\d?$/.test(newValue)) {
      target.value = this.digits[index] || '';
      return;
    }

    // Update the digit
    this.digits[index] = newValue;
    this.updateValueFromDigits();

    // Move to next input if digit entered
    if (newValue && index < this.maxLength - 1) {
      const nextInput = document.getElementById(this.getInputId(index + 1)) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    }

    this.onTouched();
  }

  onDigitKeydown(event: KeyboardEvent, index: number): void {
    const target = event.target as HTMLInputElement;

    // Handle backspace
    if (event.key === 'Backspace') {
      event.preventDefault();
      
      if (target.value) {
        // Clear current digit
        this.digits[index] = '';
        target.value = '';
        this.updateValueFromDigits();
      } else if (index > 0) {
        // Move to previous input and clear it
        this.digits[index - 1] = '';
        this.updateValueFromDigits();
        this.scheduleFocus(index - 1, { clearValue: true });
      }
    }

    // Handle Delete key
    if (event.key === 'Delete') {
      event.preventDefault();
      this.digits[index] = '';
      target.value = '';
      this.updateValueFromDigits();
    }

    // Handle arrow keys
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      const prevInput = document.getElementById(this.getInputId(index - 1)) as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
        prevInput.select();
      }
    }

    if (event.key === 'ArrowRight' && index < this.maxLength - 1) {
      event.preventDefault();
      const nextInput = document.getElementById(this.getInputId(index + 1)) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    }

    // Handle Home key
    if (event.key === 'Home') {
      event.preventDefault();
      const firstInput = document.getElementById(this.getInputId(0)) as HTMLInputElement;
      if (firstInput) {
        firstInput.focus();
        firstInput.select();
      }
    }

    // Handle End key
    if (event.key === 'End') {
      event.preventDefault();
      const lastInput = document.getElementById(this.getInputId(this.maxLength - 1)) as HTMLInputElement;
      if (lastInput) {
        lastInput.focus();
        lastInput.select();
      }
    }

    // Handle Enter key
    if (event.key === 'Enter' && this.isComplete()) {
      this.codeComplete.emit(this.value);
    }

  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData.replace(/\D/g, '').slice(0, this.maxLength);

    // Fill inputs with pasted digits
    for (let i = 0; i < this.maxLength; i++) {
      this.digits[i] = digits[i] || '';
      const input = document.getElementById(this.getInputId(i)) as HTMLInputElement;
      if (input) {
        input.value = this.digits[i];
      }
    }

    this.updateValueFromDigits();

    // Focus last filled input or first empty input
    const lastFilledIndex = Math.min(digits.length - 1, this.maxLength - 1);
    const focusIndex = digits.length >= this.maxLength ? this.maxLength - 1 : lastFilledIndex + 1;
    
    const focusInput = document.getElementById(this.getInputId(Math.max(0, focusIndex))) as HTMLInputElement;
    if (focusInput) {
      focusInput.focus();
      focusInput.select();
    }

    this.onTouched();
  }

  private updateValueFromDigits(): void {
    this.value = this.digits.join('');
    this.onChange(this.value);
    this.codeChange.emit(this.value);

    if (this.isComplete()) {
      this.codeComplete.emit(this.value);
    }
  }

  private updateDigitsFromValue(): void {
    const valueDigits = this.value.split('');
    for (let i = 0; i < this.maxLength; i++) {
      this.digits[i] = valueDigits[i] || '';
    }
  }

  getInputId(index: number): string {
    return `two-factor-digit-${this.componentId}-${index}`;
  }

  private isComplete(): boolean {
    return this.value.length === this.maxLength;
  }

  trackByIndex(index: number): number {
    return index;
  }

  // Utility methods
  clear(): void {
    this.digits.fill('');
    this.updateValueFromDigits();
    this.syncInputs();
    
    // Focus first input
    this.scheduleFocus(0, { select: false });
  }

  focus(): void {
    const firstEmptyIndex = this.digits.findIndex(digit => !digit);
    const focusIndex = firstEmptyIndex >= 0 ? firstEmptyIndex : 0;

    const input = document.getElementById(this.getInputId(focusIndex)) as HTMLInputElement;
    if (input) {
      input.focus();
      input.select();
      return;
    }

    this.scheduleFocus(focusIndex);
  }

  private focusFirstInput(): void {
    const input = document.getElementById(this.getInputId(0)) as HTMLInputElement;
    if (input) {
      input.focus();
      input.select();
      return;
    }

    this.scheduleFocus(0);
  }

  private scheduleFocus(index: number, options: { clearValue?: boolean; select?: boolean } = {}): void {
    this.clearScheduledFocus();
    this.scheduledFocusRef = afterNextRender({
      write: () => {
        this.scheduledFocusRef = undefined;
        if (this.destroyRef.destroyed) {
          return;
        }

        const input = document.getElementById(this.getInputId(index)) as HTMLInputElement;
        if (input) {
          if (options.clearValue) {
            input.value = '';
          }
          input.focus();
          if (options.select !== false) {
            input.select();
          }
        }
      }
    }, { injector: this.injector });
  }

  private scheduleSyncInputs(): void {
    this.clearScheduledSync();
    this.scheduledSyncRef = afterNextRender({
      write: () => {
        this.scheduledSyncRef = undefined;
        if (!this.destroyRef.destroyed) {
          this.syncInputs();
        }
      }
    }, { injector: this.injector });
  }

  private clearScheduledFocus(): void {
    if (!this.scheduledFocusRef) {
      return;
    }

    this.scheduledFocusRef.destroy();
    this.scheduledFocusRef = undefined;
  }

  private clearScheduledSync(): void {
    if (!this.scheduledSyncRef) {
      return;
    }

    this.scheduledSyncRef.destroy();
    this.scheduledSyncRef = undefined;
  }

  private syncInputs(): void {
    for (let i = 0; i < this.maxLength; i += 1) {
      const input = document.getElementById(this.getInputId(i)) as HTMLInputElement;
      if (input) {
        input.value = this.digits[i] || '';
      }
    }
  }

  // Dynamic class getters for cleaner template
  get containerClasses(): { [key: string]: boolean } {
    return {
      'gap-1': this.style === 'compact',
      'gap-3': this.style === 'spread'
    };
  }

  get inputClasses(): { [key: string]: boolean } {
    return {
      // Small size
      'w-8': this.size === 'small',
      'h-10': this.size === 'small',
      'text-base': this.size === 'small',
      // Medium size
      'w-10': this.size === 'medium',
      'h-12': this.size === 'medium',
      'text-lg': this.size === 'medium',
      // Large size
      'w-12': this.size === 'large',
      'h-14': this.size === 'large',
      'text-xl': this.size === 'large'
    };
  }
} 
